/* eslint-disable max-nested-callbacks,no-undef,no-return-assign, jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-tabindex */
import React from 'react';
import PropTypes from 'prop-types';
import { UX2, constants } from '@wsb/guac-widget-core';

import dataAids from '../../constants/dataAids';
import { PDFJS_SCRIPT, PDFJS_WORKER } from '../../constants/urls';
const { PUBLISH } = constants.renderModes;

class PDFViewer extends React.Component {
  constructor() {
    super(...arguments);
    this.state = {
      pageNum: 1,
      pageRendering: false,
      pageNumPending: null,
      numPages: 0,
      isHorizontal: false
    };
    this.canvas = React.createRef();
    this.canvasWrapper = React.createRef();
  }

  componentDidMount() {
    this.loadPDFJS();
    const { isMobile } = this.props;
    if (typeof window !== void 0) {
      if (!isMobile && window.innerWidth > 767 && window.IntersectionObserver) {
        this.focusObserver = new window.IntersectionObserver(entries => {
          if (entries && entries[0] && entries[0].isIntersecting) {
            entries[0].target.focus({ preventScroll: true });
          }
        });
        this.focusObserver.observe(this.canvasWrapper.current);
      }
    }
  }

  componentWillUnmount() {
    this.focusObserver && this.focusObserver.unobserve(this.canvasWrapper.current);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.pdfUrl !== this.props.pdfUrl) {
      this.setState(
        {
          pageNum: 1,
          pageRendering: false,
          pageNumPending: null,
          numPages: 0,
          isHorizontal: false
        },
        () => {
          this.renderPage(1);
        }
      );
    }
  }

  loadPDFJS = () => {
    if (typeof window !== 'undefined' && window.pdfjsLib) {
      this.renderPage(this.state.pageNum);
    } else {
      this.scriptEl = document.createElement('script');
      this.scriptEl.src = PDFJS_SCRIPT;
      this.scriptEl.onload = this.pdfjsLoaded;
      document.head.appendChild(this.scriptEl);
    }
  };

  pdfjsLoaded = () => {
    pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
    this.renderPage(this.state.pageNum);
  };

  renderPage = pageNum => {
    const { renderMode, pdfUrl } = this.props;
    const { pageNumPending } = this.state;
    this.setState({ pageRendering: true });
    this.getArrayBuffer(pdfUrl, byteArray => {
      const loadingTask = pdfjsLib.getDocument({ data: byteArray });
      loadingTask.promise.then(
        pdf => {
          pdf.getPage(pageNum).then(page => {
            const { isHorizontal, renderContext } = this.getCanvasProperties(page, pageNum);
            this.setState({ isHorizontal, numPages: pdf.numPages }, () => {
              page
                .render(renderContext)
                .promise.then(() => {
                  if (pageNumPending !== null) {
                    const pendingPage = pageNumPending;
                    this.setState({ pageNumPending: null }, () => {
                      this.renderPage(pendingPage);
                    });
                  } else {
                    this.setState({ pageRendering: false });
                    if (renderMode === PUBLISH && typeof window !== 'undefined') {
                      const tcclString = UX2.utils.TCCLUtils.getTCCLString({
                        eid: 'ux2.pdf.preview.impression',
                        type: 'impression',
                        data: {
                          pageNum: `${pageNum}`,
                          numPages: `${pdf.numPages}`,
                          layout: isHorizontal ? 'horizontal' : 'vertical'
                        }
                      });
                      window.logTcclEvent?.(null, tcclString);
                    }
                  }
                })
                .catch(err => {
                  console.error(err); // eslint-disable-line no-console
                  if (page) {
                    // bypass loading state if page loaded but an error occurred
                    this.setState({ pageRendering: false });
                  }
                });
            });
          });
        },
        err => {
          console.error(err); // eslint-disable-line no-console
        }
      );
    });
  };

  getArrayBuffer(url, cb) {
    const request = new XMLHttpRequest();
    request.open('GET', url, true);
    request.responseType = 'arraybuffer';

    request.onload = () => {
      const arrayBuffer = request.response;
      if (arrayBuffer) {
        return cb(new Uint8Array(arrayBuffer));
      }
    };

    request.send(null);
  }

  getCanvasProperties(page, pageNum) {
    const canvas = this.canvas.current;
    const viewport = page.getViewport({ scale: 2.5 });
    const context = canvas.getContext('2d');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    // Only change isHorizontal for first page:
    const isHorizontal = pageNum === 1 ? canvas.width > canvas.height : this.state.isHorizontal;
    const renderContext = {
      canvasContext: context,
      viewport: viewport
    };

    return { isHorizontal, renderContext };
  }

  renderLoadingState() {
    const { staticContent } = this.props;
    const styles = {
      block: {
        'height': '100%',
        'display': 'flex',
        'flexDirection': 'column',
        'alignItems': 'center',
        'justifyContent': 'center',
        'backgroundColor': 'neutralOverlay',
        'position': 'absolute',
        'top': '0',
        'width': '100%',
        'color': 'accent',
        '> p': {
          color: 'accent'
        }
      }
    };

    return (
      <UX2.Element.Block category='primary' style={ styles.block }>
        <UX2.Element.Loader data-aid={ dataAids.PDF_LOADING_ANIMATION } />
        <UX2.Element.Text children={ staticContent.loadingStateMessage } />
      </UX2.Element.Block>
    );
  }

  renderLinkOverlay() {
    const { pdfUrl } = this.props;
    const styles = {
      'display': 'block',
      'height': '100%',
      'position': 'absolute',
      'top': '0',
      'width': '100%',
      '@sm': {
        display: 'none'
      }
    };

    return (
      <UX2.Element.Link
        data-aid={ dataAids.PDF_LINK_OVERLAY }
        target='_blank'
        href={ pdfUrl }
        style={ styles }
      />
    );
  }

  renderPageNavigation() {
    const { pageNum, numPages, isHorizontal } = this.state;
    const { staticContent } = this.props;
    const showPrevButton = pageNum > 1;
    const showNextButton = pageNum < numPages;
    const styles = {
      block: {
        'marginTop': 'large',
        'textAlign': 'center',
        'display': 'flex',
        'alignItems': 'center',
        'justifyContent': 'center',
        'color': 'primary',
        '@xs-only': {
          display: 'none'
        }
      },
      button: {
        'display': 'inline-flex',
        'outline': 'none',
        'alignItems': 'center',
        '> *': {
          color: 'primary'
        }
      },
      buttonWrapper: {
        display: 'inline-flex',
        alignItems: 'center'
      },
      pipe: {
        marginHorizontal: 'small',
        backgroundColor: 'section'
      },
      pageNotation: {
        color: 'primary',
        lineHeight: 'inherit'
      }
    };
    const prevBtnTCCLString = UX2.utils.TCCLUtils.getTCCLString({
      eid: 'ux2.pdf.prev_button.click',
      type: 'click',
      data: {
        pageNum: `${pageNum - 1}`,
        numPages: `${numPages}`,
        layout: isHorizontal ? 'horizontal' : 'vertical'
      }
    });
    const nextBtnTCCLString = UX2.utils.TCCLUtils.getTCCLString({
      eid: 'ux2.pdf.next_button.click',
      type: 'click',
      data: {
        pageNum: `${pageNum + 1}`,
        numPages: `${numPages}`,
        layout: isHorizontal ? 'horizontal' : 'vertical'
      }
    });
    return (
      <UX2.Element.Block style={ styles.block }>
        { showPrevButton && (
          <UX2.Element.Block style={ styles.buttonWrapper }>
            <UX2.Element.MoreLink.Backward
              data-tccl={ prevBtnTCCLString }
              onClick={ this.queueRenderPage.bind(this, pageNum - 1) }
              data-aid={ dataAids.PDF_PREV_BTN_RENDERED }
              children={ staticContent.previous }
              style={ styles.button }
              data-edit-interactive={ true }
              tabIndex={ 0 }
              onKeyDown={ this.handleKeyDown }
            />
            <UX2.Element.Pipe category='primary' style={ styles.pipe } />
          </UX2.Element.Block>
        ) }
        <UX2.Element.Text
          style={ styles.pageNotation }
          data-aid={ dataAids.PDF_PAGE_NOTATION_RENDERED }
        >
          { `${pageNum}/${numPages}` }
        </UX2.Element.Text>
        { showNextButton && (
          <UX2.Element.Block style={ styles.buttonWrapper }>
            <UX2.Element.Pipe category='primary' style={ styles.pipe } />
            <UX2.Element.MoreLink.Forward
              data-tccl={ nextBtnTCCLString }
              onClick={ this.queueRenderPage.bind(this, pageNum + 1) }
              data-aid={ dataAids.PDF_NEXT_BTN_RENDERED }
              children={ staticContent.next }
              style={ styles.button }
              data-edit-interactive={ true }
              tabIndex={ 0 }
              onKeyDown={ this.handleKeyDown }
            />
          </UX2.Element.Block>
        ) }
      </UX2.Element.Block>
    );
  }

  handleKeyDown = e => {
    const { pageNum, numPages } = this.state;
    const keyCode = e && (e.keyCode || e.code);
    const previousPage = keyCode === 37 && pageNum > 1;
    const nextPage = keyCode === 39 && pageNum < numPages;
    if (previousPage) {
      this.queueRenderPage(pageNum - 1);
    }
    if (nextPage) {
      this.queueRenderPage(pageNum + 1);
    }
  };

  queueRenderPage(pageNum) {
    const { pageRendering } = this.state;

    this.setState({ pageNum: pageNum }, () => {
      if (pageRendering) {
        this.setState({ pageNumPending: pageNum });
      } else {
        this.renderPage(pageNum);
      }
    });
  }

  generateStyles() {
    return {
      gridCell: {
        '@xs-only': {
          display: 'flex',
          justifyContent: 'center'
        }
      },
      block: {
        'display': 'flex',
        'backgroundColor': 'section',
        'padding': 'xxsmall',
        '> div > canvas': {
          width: '100%',
          height: '100%'
        }
      },
      canvasWrapper: {
        width: '100%',
        position: 'relative',
        outline: 'none'
      }
    };
  }

  render() {
    const { section } = this.props;
    const { pageNum, numPages, isHorizontal, pageRendering } = this.state;
    const showPrevButton = pageNum > 1;
    const showNextButton = pageNum < numPages;
    const styles = this.generateStyles(showPrevButton, showNextButton);

    return (
      <UX2.Component.Grid.Cell style={ styles.gridCell } inset={ !isHorizontal }>
        <UX2.Element.Block section={ section === 'default' ? 'alt' : 'default' } style={ styles.block }>
          <div
            style={ styles.canvasWrapper }
            ref={ this.canvasWrapper }
            tabIndex={ 0 }
            onKeyDown={ this.handleKeyDown }
          >
            <canvas data-aid={ dataAids.PDF_PREVIEW_RENDERED } ref={ this.canvas } />
            { pageRendering && this.renderLoadingState() }
            { this.renderLinkOverlay() }
          </div>
        </UX2.Element.Block>
        { numPages > 1 && this.renderPageNavigation() }
      </UX2.Component.Grid.Cell>
    );
  }
}

export default PDFViewer;

PDFViewer.propTypes = {
  pdfUrl: PropTypes.string.isRequired,
  staticContent: PropTypes.object,
  category: PropTypes.string,
  section: PropTypes.string,
  renderMode: PropTypes.string,
  isMobile: PropTypes.bool
};
